import { AddOnVisibilityOperationType, DialogType, SubscriptionAddonCode } from '../../common/enums';
import { AddReachabilityWithMobileAddon } from './AddReachabilityWithMobileAddon';
import { EcommerceEventTypeV4 } from '../../common/analytics';
import { FormProvider, useForm } from 'react-hook-form';
import { ReachabilityWithMobileEdit } from './ReachabilityWithMobileEdit';
import { ReachabilityWithMobileView } from './ReachabilityWithMobileView';
import { SubscriptionStatusType } from '../../generated/api/subscriptionStatusType';
import { changeSubscriptionFeatures } from '../../common/fetch';
import { generatePath, useNavigate } from 'react-router-dom';
import { getAddOnsByCodes } from '../SubscriptionDetails/addOnDependencyUtils';
import {
  getReachabilityAddonAttributes,
  getReachabilityWithMobileFormValues,
  getReachabilityWithMobilePayload,
  pushAnalyticsEvent,
  workingHourErrors,
} from '../PbxAccordionContent/PbxAccordionUtils';
import { getSubscriptionStatus } from '../../common/utils/subscriptionUtils';
import { paths } from '../../common/constants/pathVariables';
import { showDialog, startNotification } from '../../selfservice/actions';
import { somethingWentWrongMsg, startTimeCantBeGreaterMsg, t } from '../../common/i18n';
import { useAddOnVisibility } from '../../common/hooks/useAddOnVisibility';
import { useAnalyticsId } from '../SubscriptionAdditionalService/addOnAnalyticsUtils';
import { useDispatch } from 'react-redux';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import { useState } from 'react';
import type { AssociationRecord, DependencyRecord } from '@onlinefirst/cloudsense-add-on-dependency-engine';
import type { DialogParams } from '../../common/types/dialog';
import type { ReachabilityWithMobileFormValues } from '../PbxAccordionContent/PbxAccordionUtils';
import type { Subscription } from '../../generated/api/subscription';
import type { SubscriptionAction } from '../../generated/api/subscriptionAction';

import './PbxAdditionalServices.scss';

interface ReachabilityWithMobileAccordionProps {
  pendingSubscriptionActions: SubscriptionAction[];
  pbxSolution: Subscription;
  subscription: Subscription;
  addOnRulesAssociations?: AssociationRecord[];
  addOnRulesDependencies?: DependencyRecord[];
}

export const ReachabilityWithMobileAccordion = ({
  subscription,
  pendingSubscriptionActions,
  addOnRulesAssociations,
  addOnRulesDependencies,
}: ReachabilityWithMobileAccordionProps) => {
  const { mdmId } = useSearchParams<{ mdmId?: string }>();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const analyticsBundleId = useAnalyticsId();
  const { addOnVisibilities } = useAddOnVisibility(
    subscription.commercialProductCode || '',
    AddOnVisibilityOperationType.CHANGE,
    undefined,
    mdmId
  );
  const onShowDialog = (params: DialogParams) => dispatch(showDialog(params));

  const reachabilityAddon = subscription.details?.selectedAddOns?.find(
    addon => addon.addOnCode === SubscriptionAddonCode.REACHABILITY_WITH_MOBILE
  );
  const addonAttributes = reachabilityAddon ? getReachabilityWithMobileFormValues(reachabilityAddon) : {};
  const subscriptionStatus = getSubscriptionStatus(subscription, pendingSubscriptionActions);

  const methods = useForm({
    defaultValues: addonAttributes,
    mode: 'onSubmit',
  });

  if (!reachabilityAddon) {
    return (
      <AddReachabilityWithMobileAddon
        subscriptionStatus={subscriptionStatus}
        subscription={subscription}
        addOnRulesAssociations={addOnRulesAssociations}
        addOnRulesDependencies={addOnRulesDependencies}
        addOnVisibilities={addOnVisibilities}
        analyticsBundleId={analyticsBundleId}
      />
    );
  }

  const onEdit = () => {
    if (subscription?.subscriptionStatus === SubscriptionStatusType.IN_ACTIVATION) {
      onShowDialog({ type: DialogType.MACD_FORBIDDEN_SUBSCRIPTION_IN_ACTIVATION });
      return;
    }
    if (subscriptionStatus.pendingActions) {
      onShowDialog({ type: DialogType.SUBSCRIPTION_ACTION_PENDING });
      return;
    }
    setIsEditing(true);
  };

  const onConfirm = async (values: ReachabilityWithMobileFormValues) => {
    const weekdayErrors = workingHourErrors(values?.workingHoursWeekdayStart, values.workingHoursWeekdayEnd);
    const weekEndErrors = workingHourErrors(values?.workingHoursWeekendStart, values.workingHoursWeekendEnd);
    if (weekdayErrors || weekEndErrors) {
      if (weekdayErrors) {
        methods.setError('workingHoursWeekdayEnd', { message: t.E9GS(startTimeCantBeGreaterMsg) });
      }
      if (weekEndErrors) {
        methods.setError('workingHoursWeekendEnd', { message: t.E9GS(startTimeCantBeGreaterMsg) });
      }
      return;
    }
    setIsSaving(true);

    const ChangesAddOns = getAddOnsByCodes(
      [SubscriptionAddonCode.REACHABILITY_WITH_MOBILE],
      addOnVisibilities || [],
      subscription.details?.selectedAddOns
    );
    const changesAddonAttributes = getReachabilityAddonAttributes(values);
    const payload = getReachabilityWithMobilePayload([], [], ChangesAddOns, subscription, changesAddonAttributes);

    try {
      await changeSubscriptionFeatures(payload, subscription.subscriptionId, mdmId);
      setIsSaving(false);
      setIsEditing(false);
      pushAnalyticsEvent(ChangesAddOns, EcommerceEventTypeV4.PURCHASE, analyticsBundleId);
      dispatch(startNotification(t.T29V('Change order successful'), 'success'));
      navigate(
        `${generatePath(paths.PS_MOBILE_SUBSCRIPTION, {
          subscriptionId: subscription?.subscriptionDisplayId,
        })}${location.search}`
      );
    } catch (error) {
      dispatch(startNotification(t.JDTO(somethingWentWrongMsg), 'error'));
      setIsSaving(false);
      setIsEditing(false);
    }
  };

  return isEditing ? (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onConfirm)} noValidate>
          <ReachabilityWithMobileEdit setEditing={setIsEditing} isSaving={isSaving} />
        </form>
      </FormProvider>
    </div>
  ) : (
    <div>
      <ReachabilityWithMobileView addonAttributes={addonAttributes} onEdit={onEdit} />
    </div>
  );
};
